/**
 * ComparadorProveedores — La misma lista cotizada a varios proveedores, línea por línea.
 *
 * Vive dentro de la ventana "Cotización del proveedor" como una segunda vista ("Comparar
 * proveedores") y usa su mismo lenguaje: colores de variación, "Coincide", frases de
 * sobre-material, "en la OC quedará…", pieza terminada bajo lo solicitado.
 *
 * Todo lo que muestra sale de las cotizaciones YA LEÍDAS de cada solicitud hermana
 * (GET purchaseOrder/:id/comparar-proveedores): no se vuelve a leer nada ni se inventan
 * precios. Por fila sugiere la más barata VÁLIDA (las de diámetro menor, sin stock, sin
 * sobre-material suficiente o no cotizadas no cuentan sin advertencia) y la persona puede
 * cambiarla. "Generar OC por proveedor" deja cada solicitud como OC con sólo sus líneas
 * (la misma aprobación de "Generar OC con lo aprobado").
 */
import React, { useEffect, useMemo, useState } from "react";
import backend from "../../backend";
import MedidaConMm from "../../components/design-system/MedidaConMm";

const NIVEL_COLOR = { verde: "#2ecc71", amarillo: "#f39c12", rojo: "#e74c3c" };
const clp = n => `$${Math.round(Number(n) || 0).toLocaleString("es-CL")}`;
const th = (extra = {}) => ({ padding: "6px 8px", fontSize: 10, color: "#aaa", textAlign: "left", textTransform: "uppercase", letterSpacing: ".04em", ...extra });

const sugerencia = (comp) => Object.fromEntries((comp?.filas || []).map(f => [f.clave, f.sugerida || null]));

const ComparadorProveedores = ({ comp, onAdjudicado }) => {
  const [asig, setAsig] = useState(() => sugerencia(comp));
  const [crearNueva, setCrearNueva] = useState(true);
  const [verDetalle, setVerDetalle] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setAsig(sugerencia(comp)); }, [comp]);

  const provs = comp?.proveedores || [];
  const filas = comp?.filas || [];

  // Totales de la selección actual (la conclusión del servidor es la del reparto sugerido).
  const tot = useMemo(() => {
    const provs = comp?.proveedores || [];
    const filas = comp?.filas || [];
    const por = Object.fromEntries(provs.map(p => [p.poId, { items: 0, total: 0 }]));
    let total = 0, sin = 0, riesgosas = [], distintas = 0;
    filas.forEach(f => {
      const id = asig[f.clave];
      if ((id || null) !== (f.sugerida || null)) distintas++;
      const c = id && f.celdas[id];
      if (!c?.seleccionable) { sin++; return; }
      por[id].items++; por[id].total += c.subtotal; total += c.subtotal;
      if (c.riesgosa) riesgosas.push(f);
    });
    return { por, total, sin, riesgosas, distintas };
  }, [asig, comp]);

  const todoAUno = comp?.resumen?.todoAUno || [];
  const nombre = id => provs.find(p => p.poId === id)?.corto || "—";

  const aplicar = async () => {
    setError("");
    const usados = provs.filter(p => tot.por[p.poId].items > 0);
    const descartadas = provs.filter(p => tot.por[p.poId].items === 0);
    if (!usados.length) { setError("Asigne al menos una línea."); return; }
    let aceptarRiesgos = false;
    if (tot.riesgosas.length) {
      if (!window.confirm(
        `Hay ${tot.riesgosas.length} línea(s) asignadas con riesgo:\n· ` +
        tot.riesgosas.map(f => `${f.descripcion} → ${nombre(asig[f.clave])}: ${f.celdas[asig[f.clave]].motivosRiesgo.join(" ")}`).join("\n· ") +
        "\n\n¿Las acepta de todos modos?"
      )) return;
      aceptarRiesgos = true;
    }
    const texto =
      `Se generan ${usados.length} orden(es) de compra:\n` +
      usados.map(p => `· N°${p.number} ${p.nombre}: ${tot.por[p.poId].items} ítem(s) por ${clp(tot.por[p.poId].total)} neto`).join("\n") +
      (descartadas.length ? `\n\nSe descartan (no se borran): ${descartadas.map(p => `N°${p.number} ${p.corto}`).join(", ")}.` : "") +
      (tot.sin ? `\n\n${tot.sin} línea(s) quedan sin adjudicar${crearNueva ? " y van a una solicitud nueva para otro proveedor" : ""}.` : "") +
      "\n\n¿Continuar?";
    if (!window.confirm(texto)) return;
    setTrabajando(true);
    try {
      const { data } = await backend.post(`purchaseOrder/${provs[0].poId}/adjudicar-proveedores`, {
        asignaciones: asig, aceptarRiesgos, solicitudSinAdjudicar: crearNueva && tot.sin > 0,
      });
      alert(
        `Listo. ${data.ocs.map(o => `OC N°${o.number} (${o.items} ítem${o.items > 1 ? "s" : ""}, ${clp(o.total)})`).join(" · ")}.` +
        (data.solicitudNueva ? `\nSolicitud N°${data.solicitudNueva.number} creada con lo no adjudicado: asígnele proveedor y envíela.` : "") +
        (data.pendientes?.length ? `\nSiguen esperando respuesta: ${data.pendientes.map(p => `N°${p.number}`).join(", ")}. Descártelas si ya no corresponden.` : "")
      );
      onAdjudicado?.(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
    setTrabajando(false);
  };

  if (provs.length < 2) return null;
  const con = comp.conclusion || {};

  return (
    <div>
      {/* Proveedores: cotización, plazo, pago y "todo a este proveedor" */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))`, gap: 8, marginBottom: 12 }}>
        {provs.map(p => {
          const t = todoAUno.find(x => x.poId === p.poId) || {};
          return (
            <div key={p.poId} style={{ background: "#111", border: "1px solid #2e2e2e", borderRadius: 6, padding: "8px 10px", fontSize: 11, color: "#cbd5e1" }}>
              <div style={{ color: "#fff", fontWeight: 700 }}>{p.nombre}</div>
              <div style={{ color: "#8a8a8a", fontSize: 10, marginBottom: 4 }}>
                Solicitud N°{p.number}{p.cotNumero ? ` · Cot. N°${p.cotNumero}` : ""}{p.cotFecha ? ` · ${p.cotFecha}` : ""}
                {p.vinculo === "coincidencia" ? " · misma lista por ítems" : ""}
              </div>
              <div>Plazo: <span style={{ color: p.plazo ? "#ddd" : NIVEL_COLOR.amarillo }}>{p.plazo || "no indicado"}</span></div>
              <div>Pago: <span style={{ color: p.pago ? "#ddd" : NIVEL_COLOR.amarillo }}>{p.pago || "no indicado"}</span></div>
              <div style={{ marginTop: 4, color: "#8a8a8a" }}>
                Todo a este proveedor: {clp(t.total)} · cubre {t.cubre} de {filas.length}
                {t.cubre > t.cubreSinRiesgo ? <span style={{ color: NIVEL_COLOR.rojo }}> ({t.cubre - t.cubreSinRiesgo} con riesgo)</span> : null}
              </div>
              <div style={{ marginTop: 2, color: tot.por[p.poId].items ? "#2ecc71" : "#666", fontWeight: 700 }}>
                Asignado: {tot.por[p.poId].items} ítem(s) · {clp(tot.por[p.poId].total)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Conclusión del sistema (reglas fijas sobre lo ya leído) */}
      {con.titulo && (
        <div style={{ background: "#0d1f10", border: "1px solid #14532d", borderRadius: 6, padding: "10px 12px", marginBottom: 12, fontSize: 11, color: "#cbd5e1", lineHeight: 1.45 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <b style={{ color: "#2ecc71", flex: 1 }}>Conclusión: {con.titulo}</b>
            <button type="button" onClick={() => setVerDetalle(v => !v)}
              style={{ background: "transparent", color: "#8a8a8a", border: "1px solid #2e2e2e", borderRadius: 4, padding: "2px 8px", fontSize: 10, cursor: "pointer" }}>
              {verDetalle ? "Ocultar detalle" : "Ver detalle"}
            </button>
          </div>
          {(con.parrafos || []).map((t, i) => <div key={i} style={{ marginBottom: 3 }}>{t}</div>)}
          {verDetalle && (con.porItem || []).length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {con.porItem.map((t, i) => <li key={i} style={{ marginBottom: 2 }}>{t}</li>)}
            </ul>
          )}
          {(con.riesgos || []).map((t, i) => <div key={i} style={{ marginTop: 4, color: NIVEL_COLOR.rojo }}>{t}</div>)}
          {tot.distintas > 0 && (
            <div style={{ marginTop: 6, color: NIVEL_COLOR.amarillo }}>
              Su selección cambia {tot.distintas} línea(s) respecto de la sugerida: total {clp(tot.total)} neto
              ({tot.total >= comp.resumen.total ? "+" : "−"}{clp(Math.abs(tot.total - comp.resumen.total))}).
            </div>
          )}
        </div>
      )}

      <div style={{ overflowX: "auto", marginBottom: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 380 + provs.length * 260 }}>
          <thead>
            <tr style={{ background: "#1a1a1a" }}>
              <th style={th({ width: "26%" })}>Solicitado</th>
              {provs.map(p => <th key={p.poId} style={th()}>{p.corto} · N°{p.number}</th>)}
              <th style={th({ textAlign: "center", width: 70 })} title="No comprar esta línea ahora">Sin adjudicar</th>
            </tr>
          </thead>
          <tbody>
            {filas.map(f => {
              const elegida = asig[f.clave];
              const ce = elegida && f.celdas[elegida];
              const color = ce ? (NIVEL_COLOR[ce.nivel] || "#444") : "#444";
              return (
                <tr key={f.clave} style={{ borderBottom: "1px solid #1f1f1f", borderLeft: `3px solid ${color}`, verticalAlign: "top" }}>
                  <td style={{ padding: "6px 8px", color: "#ddd" }}>
                    <div><MedidaConMm texto={f.descripcion} /></div>
                    <div style={{ fontSize: 10, color: "#8a8a8a" }}>
                      {f.cantidad} {f.unidad}
                      {ce?.cambiaDescripcion ? <span style={{ color: NIVEL_COLOR.amarillo }}> · en la OC quedará: <MedidaConMm texto={ce.descripcionOC} /></span> : null}
                    </div>
                    {(f.terminada || f.notas) && (
                      <div style={{ fontSize: 10, color: "#a3a3a3", marginTop: 2 }} title={f.notas}>
                        {f.terminada ? <span style={{ color: "#cbd5e1" }}>Pieza terminada {f.terminada}</span> : null}
                        {f.terminada && f.notas ? " · " : ""}
                        {f.notas ? f.notas.replace(/\s*·?\s*pieza terminada[^·]*/i, "").trim() : ""}
                      </div>
                    )}
                  </td>
                  {provs.map(p => {
                    const c = f.celdas[p.poId];
                    const sel = elegida === p.poId;
                    const esMasBarata = f.masBarata === p.poId;
                    const baratoRiesgo = f.masBarataAbsoluta === p.poId && c?.riesgosa;
                    return (
                      <td key={p.poId} onClick={() => c?.seleccionable && setAsig(a => ({ ...a, [f.clave]: p.poId }))}
                        style={{ padding: "6px 8px", color: "#cbd5e1", cursor: c?.seleccionable ? "pointer" : "default",
                                 background: sel ? "#241a35" : "transparent", border: sel ? "1px solid #3d2659" : "1px solid transparent" }}>
                        {!c ? <span style={{ fontSize: 10, color: "#666" }}>Sin cotización</span> : (
                          <>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                              <input type="radio" name={`adj-${f.clave}`} checked={sel} disabled={!c.seleccionable}
                                onChange={() => setAsig(a => ({ ...a, [f.clave]: p.poId }))}
                                style={{ cursor: c.seleccionable ? "pointer" : "default", accentColor: "#a78bfa", marginTop: 2 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {c.cotizado ? (
                                  c.cotizado.manual ? <div style={{ fontSize: 10, color: "#8a8a8a" }}>Precio cargado a mano</div> : (
                                    <>
                                      <div>{c.cotizado.material} · <MedidaConMm texto={c.cotizado.diametro} /></div>
                                      <div style={{ fontSize: 10, color: "#8a8a8a" }}>
                                        {c.cotizado.trozos != null ? `${c.cotizado.trozos} tr` : ""}{c.cotizado.largoMm ? ` × ${c.cotizado.largoMm} mm` : ""}
                                        {c.cotizado.cantidad ? ` · ${c.cotizado.cantidad} ${c.cotizado.unidad || ""}` : ""}
                                      </div>
                                    </>
                                  )
                                ) : <span style={{ color: NIVEL_COLOR.rojo }}>No cotizado</span>}
                              </div>
                            </div>
                            {c.cotizado && (
                              <div style={{ marginTop: 3 }}>
                                {c.variaciones.length === 0
                                  ? <span style={{ fontSize: 10, color: NIVEL_COLOR.verde }}>Coincide</span>
                                  : c.variaciones.map((v, i) => (
                                    <div key={i} style={{ fontSize: 10, color: NIVEL_COLOR[v.nivel] || "#ccc", marginBottom: 2 }}>{v.texto}</div>
                                  ))}
                                {c.motivosRiesgo.filter(m => /stock/i.test(m)).map((m, i) => (
                                  <div key={`s${i}`} style={{ fontSize: 10, color: NIVEL_COLOR.rojo }}>{m}</div>
                                ))}
                              </div>
                            )}
                            {c.seleccionable && (
                              <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", gap: 6, fontFamily: "monospace" }}>
                                <span style={{ color: "#aaa" }}>{clp(c.pUnit)}/u</span>
                                <span style={{ color: sel ? "#2ecc71" : "#ddd", fontWeight: 700 }}>{clp(c.subtotal)}</span>
                              </div>
                            )}
                            {esMasBarata && <div style={{ fontSize: 10, color: NIVEL_COLOR.verde, marginTop: 2 }}>más barata válida</div>}
                            {baratoRiesgo && <div style={{ fontSize: 10, color: NIVEL_COLOR.rojo, marginTop: 2 }}>más barata, pero con riesgo</div>}
                            {f.motivoEmpate && f.sugerida === p.poId && <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>sugerida: {f.motivoEmpate}</div>}
                          </>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: "6px 8px", textAlign: "center" }}>
                    <input type="radio" name={`adj-${f.clave}`} checked={!elegida}
                      onChange={() => setAsig(a => ({ ...a, [f.clave]: null }))}
                      style={{ cursor: "pointer", accentColor: "#a78bfa" }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "#1a1a1a" }}>
              <td style={{ padding: "8px 10px", textAlign: "right", color: "#aaa", textTransform: "uppercase", fontSize: 10, fontWeight: 700 }}>Asignado · neto</td>
              {provs.map(p => (
                <td key={p.poId} style={{ padding: "8px 10px", textAlign: "right", color: tot.por[p.poId].total ? "#2ecc71" : "#666", fontWeight: 700 }}>
                  {clp(tot.por[p.poId].total)} <span style={{ color: "#8a8a8a", fontWeight: 400, fontSize: 10 }}>({tot.por[p.poId].items})</span>
                </td>
              ))}
              <td style={{ padding: "8px 10px", textAlign: "center", color: tot.sin ? NIVEL_COLOR.amarillo : "#666", fontSize: 11 }}>{tot.sin}</td>
            </tr>
            <tr style={{ background: "#1a1a1a" }}>
              <td colSpan={provs.length + 2} style={{ padding: "4px 10px 8px", textAlign: "right", color: "#2ecc71", fontWeight: 700 }}>
                Total combinado {clp(tot.total)} <span style={{ color: "#8a8a8a", fontWeight: 400, fontSize: 10 }}>· IVA {clp(tot.total * 0.19)} · total {clp(tot.total * 1.19)}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {(comp.pendientes || []).length > 0 && (
        <div style={{ fontSize: 11, color: "#8a8a8a", marginBottom: 8 }}>
          Sin cotización leída todavía: {comp.pendientes.map(p => `N°${p.number} ${p.nombre}`).join(", ")}. Ábralas y use "Leer y comparar" para sumarlas.
        </div>
      )}

      <div style={{ fontSize: 10, color: "#cbd5e1", marginBottom: 12, lineHeight: 1.4 }}>
        <b>Generar OC por proveedor:</b> cada solicitud queda como orden de compra solo con las líneas que se le asignaron (con la medida cotizada si aceptó una variación); las que no reciben ninguna se descartan (no se borran).
        {tot.sin > 0 && (
          <label style={{ display: "block", marginTop: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={crearNueva} onChange={e => setCrearNueva(e.target.checked)} style={{ accentColor: "#a78bfa", marginRight: 6 }} />
            Crear una solicitud nueva con las {tot.sin} línea(s) sin adjudicar, para pedirlas a otro proveedor
          </label>
        )}
      </div>

      {error && <div style={{ fontSize: 11, color: NIVEL_COLOR.rojo, marginBottom: 8 }}>⚠ {error}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={() => setAsig(sugerencia(comp))} disabled={trabajando || tot.distintas === 0}
          style={{ background: "transparent", color: "#cbd5e1", border: "1px solid #444", borderRadius: 5, padding: "6px 14px", fontSize: 12, cursor: "pointer", opacity: tot.distintas === 0 ? .5 : 1 }}>
          Volver a la sugerencia
        </button>
        <button type="button" onClick={aplicar} disabled={trabajando || tot.total === 0}
          style={{ background: "#2ecc71", color: "#0d1f10", border: "none", borderRadius: 5, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: (trabajando || tot.total === 0) ? .6 : 1 }}>
          {trabajando ? "Procesando…" : `✓ Generar OC por proveedor (${provs.filter(p => tot.por[p.poId].items > 0).length})`}
        </button>
      </div>
    </div>
  );
};

export default ComparadorProveedores;
